/**
 * AnimationCanvas - Main canvas component for animation playback.
 * Renders the football field, animated routes, and player positions.
 */

import { useRef } from 'react'
import { AnimationCanvasRenderer } from './AnimationCanvasRenderer'
import { useAnimation } from '../../contexts/AnimationContext'
import { useAnimationEngine } from '@/hooks/UseAnimationEngine'
import { useDimensions, useFieldCoordinates } from '@/hooks'
import { usePlayerInfoMap } from '@/hooks/UsePlayerInfoMap'
import { useRouteProgressMap } from '@/hooks/UseRouteProgressMap'
import { shouldShowAnimation } from '@/utils/Animation.utils'
import type { Drawing, Player } from '@/types'

type AnimationCanvasProps = {
	drawings: Drawing[]
	players: Player[]
	className?: string
	onComplete?: () => void
	/** Hide the dimmed ghost path behind animated routes */
	hideGhostPath?: boolean
}

export function AnimationCanvas({
									drawings,
									players,
									className,
									onComplete,
									hideGhostPath = false,
								}: AnimationCanvasProps) {
	const containerRef = useRef<HTMLDivElement>(null)
	const dimensions = useDimensions(containerRef)
	const { state, dispatch } = useAnimation()

	const coordSystem = useFieldCoordinates({
		containerWidth: dimensions.width,
		containerHeight: dimensions.height,
	})

	useAnimationEngine({
		state,
		dispatch,
		onComplete,
	})

	const playerInfo = usePlayerInfoMap(players)
	const routeProgress = useRouteProgressMap(state.playerStates)

	if (dimensions.width === 0 || dimensions.height === 0) {
		return (
			<div ref={containerRef} className={`size-full ${className ?? ''}`.trim()} />
		)
	}

	return (
		<div
			ref={containerRef}
			className={`relative size-full overflow-hidden ${className ?? ''}`.trim()}
		>
			<AnimationCanvasRenderer
				drawings={drawings}
				animationState={state}
				coordSystem={coordSystem}
				playerInfo={playerInfo}
				routeProgress={routeProgress}
				showAnimation={shouldShowAnimation(state)}
				hideGhostPath={hideGhostPath}
			/>
		</div>
	)
}
